import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    FiArrowLeft, FiPlus, FiEdit2, FiTrash2, FiMessageSquare,
    FiX, FiCalendar, FiAlertCircle, FiCheck,
    FiList, FiSend, FiClock, FiPaperclip, FiDownload, FiFileText, FiEye, FiUsers
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { normalizeRole } from '../../finance/auth/permissions';
import { fmtDate } from '../../utils/istDate';
import ConfirmModal from '../shared/ConfirmModal';
import tasksApi, { canEditTask, canChangeTaskStatus } from '../../api/tasksApi';
import { extractErrorMessage } from '../../finance/api/client';

const STATUS_META = {
    TO_DO: { label: 'To Do', color: 'text-sky-700', bg: 'bg-sky-50', border: 'border-sky-200' },
    IN_PROGRESS: { label: 'In Progress', color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200' },
    COMPLETED: { label: 'Completed', color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' },
    BLOCKED: { label: 'Blocked', color: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-200' },
    CREATED: { label: 'To Do', color: 'text-sky-700', bg: 'bg-sky-50', border: 'border-sky-200' },
    NOT_STARTED: { label: 'To Do', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
};

export default function TaskDetails() {
    const { projectId, taskId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    
    const me = localStorage.getItem('username') || '';
    const role = localStorage.getItem('role') || '';
    const normalizedRole = normalizeRole(role);
    const isAdmin = normalizedRole === 'ADMIN' || normalizedRole === 'SUPERADMIN' || normalizedRole === 'TESTER';

    const [task, setTask] = useState(location.state?.task || null);
    const [subtasks, setSubtasks] = useState([]);
    const [comments, setComments] = useState([]);
    const [attachments, setAttachments] = useState(location.state?.task?.attachments || []);
    const [loading, setLoading] = useState(!task);
    const [error, setError] = useState('');
    
    const [activeTab, setActiveTab] = useState('subtasks');
    const [commentDraft, setCommentDraft] = useState('');

    const [showSubModal, setShowSubModal] = useState(false);
    const [subTaskTitle, setSubTaskTitle] = useState('');
    const [subTaskStatus, setSubTaskStatus] = useState('TO_DO');
    const [subTaskDescription, setSubTaskDescription] = useState('');
    const [subTaskDeadLine, setSubTaskDeadLine] = useState('');
    const [previewFile, setPreviewFile] = useState(null);

    useEffect(() => {
        if (!previewFile) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                setPreviewFile(null);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [previewFile]);

    const fetchTask = useCallback(async () => {
        if (task) return;
        setLoading(true);
        try {
            const projectTasks = await tasksApi.getProjectTasks(projectId);
            const foundTask = (projectTasks || []).find(t => String(t.taskId) === String(taskId));
            if (foundTask) {
                setTask(foundTask);
                setAttachments(foundTask.attachments || []);
            } else {
                setError('Task not found in this project.');
            }
        } catch (e) {
            setError('Failed to load task details.');
        } finally {
            setLoading(false);
        }
    }, [projectId, taskId, task]);

    const fetchSubtasks = useCallback(async () => {
        try { 
            const data = await tasksApi.getSubtasks(taskId);
            setSubtasks(Array.isArray(data) ? data : []); 
        } catch { setSubtasks([]); }
    }, [taskId]);

    const fetchComments = useCallback(async () => {
        try { 
            const data = await tasksApi.getComments(taskId);
            setComments(Array.isArray(data) ? data : []); 
        } catch { setComments([]); }
    }, [taskId]);

    useEffect(() => {
        fetchTask();
        fetchSubtasks();
        fetchComments();
    }, [fetchTask, fetchSubtasks, fetchComments]);

    const [deleteTaskConfirm, setDeleteTaskConfirm] = useState({ open: false, loading: false });

    const normalizeUsername = (u) => String(u || '').trim().toLowerCase().split('@')[0];
    const myNormUser = normalizeUsername(me);
    const taskCreatorNorm = normalizeUsername(task?.createdBy || task?.createdByName);
    const isTaskCreator = Boolean(taskCreatorNorm && myNormUser && taskCreatorNorm === myNormUser);
    const canDeleteTask = isTaskCreator;

    const canDeleteSubtask = (sub) => {
        const subCreatorNorm = normalizeUsername(sub?.createdBy);
        if (subCreatorNorm && myNormUser && subCreatorNorm === myNormUser) return true;
        if (isTaskCreator) return true;
        return false;
    };

    const updateTaskStatus = async (newStatus) => {
        if (!canEditTask(task, me)) {
            toast.error('Only the task assignee or creator can edit this task.');
            return;
        }
        try {
            await tasksApi.updateTaskStatus(taskId, newStatus);
            const title = task?.title || 'Task';
            const statusLabel = STATUS_META[newStatus]?.label || newStatus;
            toast.success(`Task "${title}" status changed to ${statusLabel}.`);
            setTask(prev => ({ ...prev, taskStatus: newStatus }));
        } catch { toast.error('Failed to update status'); }
    };

    const openAddSub = () => { 
        setSubTaskTitle(''); 
        setSubTaskStatus('TO_DO'); 
        setSubTaskDescription('');
        setSubTaskDeadLine('');
        setShowSubModal(true); 
    };
    
    const saveSub = async () => {
        try {
            if (!subTaskTitle.trim()) return toast.error('Subtask title is required');
            const titleText = subTaskTitle.trim();
            const payload = {
                taskId: Number(taskId),
                subTaskTitle: titleText,
                subTaskStatus: subTaskStatus || 'TO_DO'
            };
            if (subTaskDescription.trim()) payload.description = subTaskDescription.trim();
            if (subTaskDeadLine) payload.deadLine = subTaskDeadLine;

            await tasksApi.createSubtask(payload);
            toast.success(`Subtask "${titleText}" has been created.`);
            setShowSubModal(false); 
            setSubTaskTitle('');
            setSubTaskDescription('');
            setSubTaskDeadLine('');
            setSubTaskStatus('TO_DO');
            fetchSubtasks();
        } catch (err) { 
            toast.error(extractErrorMessage(err) || 'Failed to save subtask.'); 
        }
    };

    const handleDeleteSubtask = async (subTaskId, sTitle) => {
        try {
            await tasksApi.deleteSubtask(subTaskId);
            toast.success(`Subtask "${sTitle || 'Subtask'}" has been deleted.`);
            fetchSubtasks();
        } catch (err) {
            toast.error(extractErrorMessage(err) || 'Failed to delete subtask');
        }
    };

    const handleUpdateSubtaskStatus = async (subTaskId, newStatus, sTitle) => {
        setSubtasks(prev => prev.map(s => {
            const sId = s.subTaskId || s.id || s.taskId;
            if (String(sId) === String(subTaskId)) {
                return { ...s, subTaskStatus: newStatus, taskStatus: newStatus };
            }
            return s;
        }));
        try {
            await tasksApi.updateSubtaskStatus(subTaskId, newStatus, taskId, sTitle);
            const statusLabel = STATUS_META[newStatus]?.label || newStatus;
            toast.success(`Subtask "${sTitle || 'Subtask'}" status changed to ${statusLabel}.`);
        } catch (err) {
            toast.error(extractErrorMessage(err) || 'Failed to update subtask status');
            fetchSubtasks();
        }
    };

    const submitComment = async () => {
        const text = commentDraft.trim();
        if (!text) return;
        try {
            await tasksApi.createComment(parseInt(taskId), text);
            setCommentDraft('');
            fetchComments();
            toast.success('Comment posted successfully.');
        } catch { toast.error('Failed to post comment.'); }
    };

    const deleteComment = async (cId) => {
        try { await tasksApi.deleteComment(cId); fetchComments(); toast.success('Comment has been deleted.'); }
        catch { toast.error('Failed to delete comment.'); }
    };

    const handleAddAttachment = async (e) => {
        const selectedFiles = Array.from(e.target.files);
        if (!selectedFiles.length) return;
        try {
            for (const file of selectedFiles) {
                const newAtt = await tasksApi.uploadAttachment(taskId, file);
                toast.success(`"${file.name}" has been attached to "${task?.title || 'Task'}".`);
                if (newAtt) {
                    setAttachments(prev => [...prev, newAtt]);
                }
            }
            fetchTask();
        } catch { 
            toast.error('Failed to upload attachment.'); 
        } finally {
            e.target.value = '';
        }
    };

    const handleDeleteAttachment = async (attachmentId, fileName) => {
        try {
            await tasksApi.deleteAttachment(taskId, attachmentId);
            toast.success(`"${fileName || 'Attachment'}" deleted.`);
            setAttachments(prev => prev.filter(a => a.id !== attachmentId));
            fetchTask();
        } catch {
            toast.error('Failed to delete attachment.');
        }
    };

    const confirmDeleteTask = async () => {
        setDeleteTaskConfirm(prev => ({ ...prev, loading: true }));
        try {
            await tasksApi.deleteTask(taskId);
            const title = task?.title || 'Task';
            toast.success(`Task "${title}" has been deleted.`);
            setDeleteTaskConfirm({ open: false, loading: false });
            if (projectId) {
                navigate(`/ProjectDetails/${projectId}`);
            } else {
                navigate('/my-space/tasks');
            }
        } catch (err) {
            toast.error(extractErrorMessage(err) || 'Failed to delete task');
            setDeleteTaskConfirm(prev => ({ ...prev, loading: false }));
        }
    };

    const fmt = (d) => fmtDate(d) || null;

    if (loading) return <div className="py-20 text-center text-slate-500">Loading task details...</div>;
    if (error) return <div className="p-6 text-center text-red-500 bg-red-50 rounded-xl m-6"><FiAlertCircle className="inline mr-2"/>{error}</div>;
    if (!task) return <div className="py-20 text-center text-slate-500">Task not found.</div>;

    const sm = STATUS_META[task.taskStatus] || STATUS_META.TO_DO;

    return (
        <div className="max-w-5xl mx-auto space-y-6 animate-fade-in pb-20">
            <button 
                className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors"
                onClick={() => navigate(`/ProjectDetails/${projectId}`)}
            >
                <FiArrowLeft /> Back to Project
            </button>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-6 md:p-8 flex flex-col md:flex-row md:items-start justify-between gap-6 border-b border-slate-100 relative">
                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                            <span className={`px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wider border ${sm.bg} ${sm.color} ${sm.border}`}>
                                {sm.label}
                            </span>
                            <span className="text-sm font-medium text-slate-400">Task #{task.taskId}</span>
                        </div>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3">{task.title}</h1>
                        {task.description && <p className="text-slate-600 leading-relaxed mb-6">{task.description}</p>}
                        
                        <div className="flex flex-wrap gap-6 text-sm">
                            <div className="flex items-center gap-2 text-slate-600">
                                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-500 border border-slate-200">
                                    {task.username ? task.username.substring(0,2).toUpperCase() : '?'}
                                </div>
                                <div>
                                    <div className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Assignee</div>
                                    <div className="font-medium">{task.username || 'Unassigned'}</div>
                                </div>
                            </div>
                            {task.deadLine && (
                                <div className="flex items-center gap-2 text-slate-600">
                                    <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-500 border border-blue-100">
                                        <FiCalendar />
                                    </div>
                                    <div>
                                        <div className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Deadline</div>
                                        <div className="font-medium">{fmt(task.deadLine)}</div>
                                    </div>
                                </div>
                            )}
                            {Array.isArray(task.copyTo) && task.copyTo.length > 0 && (
                                <div className="flex items-center gap-2 text-slate-600">
                                    <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center text-purple-600 border border-purple-100">
                                        <FiUsers />
                                    </div>
                                    <div>
                                        <div className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Copy To ({task.copyTo.length})</div>
                                        <div className="flex flex-wrap gap-1 mt-0.5">
                                            {task.copyTo.map((c, i) => (
                                                <span key={c.id || c.username || i} className="inline-flex items-center px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-700 font-medium">
                                                    {c.fullName || c.username}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    <div className="flex flex-col gap-3 shrink-0 w-full md:w-48">
                        <div>
                            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5 block">Update Status</label>
                            <select
                                className={`w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                                    !canEditTask(task, me) ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'
                                }`}
                                value={task.taskStatus || 'TO_DO'}
                                onChange={e => updateTaskStatus(e.target.value)}
                                disabled={!canEditTask(task, me)}
                                title={canEditTask(task, me) ? 'Change status' : 'Editing is restricted to assignee and creator'}
                            >
                                <option value="TO_DO">To Do</option>
                                <option value="IN_PROGRESS">In Progress</option>
                                <option value="COMPLETED">Completed</option>
                                <option value="BLOCKED">Blocked</option>
                            </select>
                        </div>

                        {canEditTask(task, me) && (
                            <button 
                                onClick={() => navigate(`/ProjectDetails/${projectId}/task/${task.taskId}/edit`, { state: { task } })}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 font-medium text-sm transition-colors"
                            >
                                <FiEdit2 size={14} /> Edit Details
                            </button>
                        )}
                        {canDeleteTask && (
                            <button
                                onClick={() => setDeleteTaskConfirm({ open: true, loading: false })}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-rose-200 text-rose-600 bg-rose-50 rounded-xl hover:bg-rose-100 font-bold text-xs transition-colors"
                            >
                                <FiTrash2 size={14} /> Delete Task
                            </button>
                        )}
                    </div>
                </div>

                <div className="flex border-b border-slate-200 bg-slate-50/50">
                    <button 
                        className={`px-6 py-4 text-sm font-semibold flex items-center gap-2 border-b-2 transition-colors ${activeTab === 'subtasks' ? 'border-blue-600 text-blue-700 bg-white' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
                        onClick={() => setActiveTab('subtasks')}
                    >
                        <FiList /> Subtasks
                        <span className="bg-slate-200/50 text-slate-600 px-2 py-0.5 rounded-full text-xs ml-1">{subtasks.length}</span>
                    </button>
                    <button 
                        className={`px-6 py-4 text-sm font-semibold flex items-center gap-2 border-b-2 transition-colors ${activeTab === 'comments' ? 'border-blue-600 text-blue-700 bg-white' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
                        onClick={() => setActiveTab('comments')}
                    >
                        <FiMessageSquare /> Discussion
                        <span className="bg-slate-200/50 text-slate-600 px-2 py-0.5 rounded-full text-xs ml-1">{comments.length}</span>
                    </button>
                    <button 
                        className={`px-6 py-4 text-sm font-semibold flex items-center gap-2 border-b-2 transition-colors ${activeTab === 'attachments' ? 'border-blue-600 text-blue-700 bg-white' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
                        onClick={() => setActiveTab('attachments')}
                    >
                        <FiPaperclip /> Attachments
                        <span className="bg-slate-200/50 text-slate-600 px-2 py-0.5 rounded-full text-xs ml-1">{attachments.length}</span>
                    </button>
                </div>

                <div className="p-6 bg-slate-50/30">
                    {activeTab === 'subtasks' && (() => {
                        const completedCount = subtasks.filter(s => (s.subTaskStatus || s.taskStatus) === 'COMPLETED').length;
                        const totalCount = subtasks.length;

                        return (
                            <div className="space-y-4">
                                <div className="flex justify-between items-center mb-2">
                                    <h3 className="font-bold text-slate-700">Task Breakdown — {completedCount}/{totalCount} completed</h3>
                                    <button onClick={openAddSub} className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1.5 bg-blue-50 px-3 py-1.5 rounded-lg transition-colors">
                                        <FiPlus /> Add Subtask
                                    </button>
                                </div>
                                
                                {totalCount === 0 ? (
                                    <div className="text-center py-12 bg-white rounded-xl border border-dashed border-slate-200">
                                        <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3 text-slate-400">
                                            <FiList size={20} />
                                        </div>
                                        <p className="text-slate-500 font-medium">No subtasks yet.</p>
                                    </div>
                                ) : (
                                    <div className="grid gap-3">
                                        {subtasks.map(sub => {
                                            const sId = sub.subTaskId || sub.id || sub.taskId;
                                            const sTitle = sub.subTaskTitle || sub.title;
                                            const sStatus = sub.subTaskStatus || sub.taskStatus || 'TO_DO';

                                            return (
                                                <div key={sId} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-white border border-slate-200 rounded-xl">
                                                    <div className="flex items-center gap-3">
                                                        <div 
                                                            onClick={() => handleUpdateSubtaskStatus(sId, sStatus === 'COMPLETED' ? 'TO_DO' : 'COMPLETED', sTitle)}
                                                            title="Click to toggle status"
                                                            className={`w-5 h-5 rounded flex items-center justify-center border cursor-pointer transition-all ${
                                                                sStatus === 'COMPLETED'
                                                                    ? 'bg-emerald-500 border-emerald-600 text-white'
                                                                    : sStatus === 'IN_PROGRESS'
                                                                    ? 'bg-amber-500 border-amber-600 text-white'
                                                                    : 'bg-rose-50 border-rose-300 text-rose-500 hover:bg-rose-100'
                                                            }`}
                                                        >
                                                            {sStatus === 'COMPLETED' && <FiCheck className="text-xs" />}
                                                            {sStatus === 'IN_PROGRESS' && <span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>}
                                                        </div>
                                                        <div>
                                                            <span className={`font-semibold ${sStatus === 'COMPLETED' ? 'text-slate-700' : 'text-slate-800'}`}>
                                                                {sTitle}
                                                            </span>
                                                            {sub.description && <p className="text-xs text-slate-500 mt-0.5">{sub.description}</p>}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-3 self-end sm:self-auto">
                                                        {sub.deadLine && (
                                                            <span className="text-xs text-slate-500 flex items-center gap-1">
                                                                <FiClock /> {fmt(sub.deadLine)}
                                                            </span>
                                                        )}

                                                        <select
                                                            value={sStatus}
                                                            onChange={(e) => handleUpdateSubtaskStatus(sId, e.target.value, sTitle)}
                                                            className={`px-3 py-1.5 text-xs font-bold rounded-xl border cursor-pointer outline-none transition-all ${
                                                                sStatus === 'COMPLETED'
                                                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                                                    : sStatus === 'IN_PROGRESS'
                                                                    ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                                                                    : 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100'
                                                            }`}
                                                        >
                                                            <option value="TO_DO" className="bg-white text-slate-800 font-medium">To Do</option>
                                                            <option value="IN_PROGRESS" className="bg-white text-slate-800 font-medium">In Progress</option>
                                                            <option value="COMPLETED" className="bg-white text-slate-800 font-medium">Completed</option>
                                                        </select>

                                                        {canDeleteSubtask(sub) && (
                                                            <button
                                                                onClick={() => handleDeleteSubtask(sId, sTitle)}
                                                                className="p-1.5 text-slate-400 hover:text-rose-600 transition-colors rounded-lg hover:bg-rose-50"
                                                                title="Delete Subtask"
                                                            >
                                                                <FiTrash2 size={14} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {activeTab === 'comments' && (
                        <div className="space-y-4 max-w-2xl">
                            <div className="space-y-3 max-h-[280px] overflow-y-auto pr-1">
                                {comments.length === 0 ? (
                                    <div className="text-center py-8 bg-white rounded-xl border border-dashed border-slate-200">
                                        <FiMessageSquare className="text-2xl text-slate-300 mx-auto mb-1" />
                                        <p className="text-slate-500 text-xs font-semibold">No discussion comments yet.</p>
                                    </div>
                                ) : (
                                    comments.map((c, idx) => {
                                        const commentText = typeof c === 'string' ? c : (c.description || c.comment || c.text || c.content || c.commentText || c.body || c.message || '');
                                        const commentUser = c.username || c.user || c.author || 'U';
                                        const cId = c.commentId || c.id || idx;
                                        const isMe = commentUser === me;
                                        return (
                                            <div key={cId} className={`flex gap-3 ${isMe ? 'flex-row-reverse' : ''}`}>
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 shadow-xs ${isMe ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-700'}`}>
                                                    {commentUser ? commentUser.substring(0,2).toUpperCase() : 'U'}
                                                </div>
                                                <div className={`group relative max-w-[85%] ${isMe ? 'text-right' : 'text-left'}`}>
                                                    <div className="text-[11px] font-semibold text-slate-400 mb-0.5 px-1">
                                                        {isMe ? 'You' : commentUser}
                                                    </div>
                                                    <div className={`relative px-3.5 py-2 text-xs shadow-xs ${isMe ? 'bg-blue-600 text-white rounded-xl rounded-tr-xs' : 'bg-white border border-slate-200 text-slate-700 rounded-xl rounded-tl-xs'}`}>
                                                        <span className="whitespace-pre-wrap">{commentText || '(No content)'}</span>
                                                        {(isMe || isAdmin) && (c.commentId || c.id) && (
                                                            <button 
                                                                onClick={() => deleteComment(c.commentId || c.id)} 
                                                                className={`absolute top-1/2 -translate-y-1/2 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity bg-white shadow-xs border border-slate-100 text-slate-400 hover:text-red-500 ${isMe ? '-left-8' : '-right-8'}`}
                                                            >
                                                                <FiTrash2 size={12} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                            
                            <div className="flex items-center gap-2 bg-white p-1.5 rounded-xl border border-slate-200 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500 transition-all shadow-xs">
                                <input 
                                    type="text"
                                    className="w-full bg-transparent px-3 py-1.5 text-xs outline-none text-slate-800 placeholder:text-slate-400"
                                    placeholder="Write a comment..."
                                    value={commentDraft}
                                    onChange={e => setCommentDraft(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' && commentDraft.trim()) {
                                            submitComment();
                                        }
                                    }}
                                />
                                <button 
                                    onClick={submitComment}
                                    disabled={!commentDraft.trim()}
                                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg font-bold text-xs hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-1 shrink-0"
                                >
                                    <FiSend size={12} /> Post
                                </button>
                            </div>
                        </div>
                    )}

                    {activeTab === 'attachments' && (
                        <div className="space-y-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="font-bold text-slate-800 text-base">Attachments</h3>
                                </div>
                                <label className="cursor-pointer inline-flex items-center gap-1.5 px-4 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-xl text-xs font-bold transition-colors">
                                    <FiPlus /> Add Attachment
                                    <input type="file" multiple className="hidden" onChange={handleAddAttachment} />
                                </label>
                            </div>

                            {attachments.length === 0 ? (
                                <div className="text-center py-12 bg-white rounded-xl border border-dashed border-slate-200">
                                    <p className="text-slate-500 text-sm font-semibold">No attachments yet.</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {attachments.map(att => (
                                        <div key={att.id || att.fileName} className="p-3.5 bg-white rounded-xl border border-slate-200 flex items-center justify-between">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="w-9 h-9 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center text-lg shrink-0">
                                                    <FiFileText />
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="text-xs font-bold text-slate-700 truncate">{att.fileName}</div>
                                                    <div className="text-[10px] text-slate-400">{att.uploadedAt ? fmtDate(att.uploadedAt) : ''}</div>
                                                </div>
                                            </div>
                                            {att.fileUrl && (
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        onClick={() => setPreviewFile(att)}
                                                        className="px-2.5 py-1 text-xs font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors inline-flex items-center gap-1"
                                                        title="View PDF / Document"
                                                    >
                                                        <FiEye size={13} /> View
                                                    </button>
                                                    <a href={att.fileUrl} target="_blank" rel="noreferrer" download={att.fileName} className="p-1.5 text-slate-400 hover:text-blue-600 rounded-lg transition-colors" title="Download file">
                                                        <FiDownload size={14} />
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Subtask Modal */}
            {showSubModal && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden p-6 space-y-4">
                        <div className="flex justify-between items-center border-b pb-3">
                            <h3 className="text-lg font-bold text-slate-800">New Subtask</h3>
                            <button onClick={() => setShowSubModal(false)}><FiX /></button>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Title *</label>
                            <input value={subTaskTitle} onChange={e => setSubTaskTitle(e.target.value)} className="w-full px-3 py-2 border rounded-xl text-sm" placeholder="Subtask title..." />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Description (Optional)</label>
                            <textarea value={subTaskDescription} onChange={e => setSubTaskDescription(e.target.value)} className="w-full px-3 py-2 border rounded-xl text-sm" placeholder="Details..." rows={2} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Status</label>
                                <select value={subTaskStatus} onChange={e => setSubTaskStatus(e.target.value)} className="w-full px-3 py-2 border rounded-xl text-sm font-medium">
                                    <option value="TO_DO">To Do</option>
                                    <option value="IN_PROGRESS">In Progress</option>
                                    <option value="COMPLETED">Completed</option>
                                    <option value="BLOCKED">Blocked</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Deadline</label>
                                <input type="date" value={subTaskDeadLine} onChange={e => setSubTaskDeadLine(e.target.value)} className="w-full px-3 py-2 border rounded-xl text-sm" />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={() => setShowSubModal(false)} className="px-4 py-2 text-xs font-bold text-slate-600 bg-slate-100 rounded-xl">Cancel</button>
                            <button onClick={saveSub} className="px-4 py-2 text-xs font-bold text-white bg-blue-600 rounded-xl">Save Subtask</button>
                        </div>
                    </div>
                </div>
            )}
            {/* PDF / Document Preview Modal */}
            {previewFile && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-3 bg-black/25"
                    onClick={() => setPreviewFile(null)}
                >
                    <div
                        className="bg-white rounded-xl w-[96vw] max-w-[1760px] h-[95vh] shadow-2xl flex flex-col overflow-hidden border border-slate-200"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="px-4 py-2 border-b border-slate-200 flex items-center justify-between bg-white shrink-0">
                            <div className="flex items-center gap-2.5 min-w-0 pr-4">
                                <FiFileText className="text-blue-600 shrink-0 text-lg" />
                                <h3 className="font-semibold text-slate-800 truncate text-sm sm:text-base" title={previewFile.fileName}>
                                    {previewFile.fileName}
                                </h3>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <a
                                    href={previewFile.fileUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="px-3 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors inline-flex items-center gap-1"
                                >
                                    Open in New Tab
                                </a>
                                <a
                                    href={previewFile.fileUrl}
                                    download={previewFile.fileName}
                                    className="px-3 py-1.5 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors inline-flex items-center gap-1"
                                >
                                    <FiDownload size={13} /> Download
                                </a>
                                <button
                                    onClick={() => setPreviewFile(null)}
                                    className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100 transition-colors ml-1"
                                    title="Close (Esc)"
                                >
                                    <FiX className="text-lg" />
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 bg-white p-0 overflow-hidden relative">
                            {previewFile.fileUrl ? (
                                /\.(png|jpe?g|gif|webp|svg)$/i.test(previewFile.fileName || '') ? (
                                    <div className="w-full h-full flex items-center justify-center p-2 bg-slate-50/50 overflow-auto">
                                        <img
                                            src={previewFile.fileUrl}
                                            alt={previewFile.fileName}
                                            className="max-w-full max-h-full object-contain"
                                        />
                                    </div>
                                ) : (
                                    <iframe
                                        src={previewFile.fileUrl}
                                        title={previewFile.fileName}
                                        className="w-full h-full border-0 block"
                                    />
                                )
                            ) : (
                                <div className="text-center py-20 text-slate-500">
                                    <p>Preview not available for this file.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <ConfirmModal
                isOpen={deleteTaskConfirm.open}
                onClose={() => setDeleteTaskConfirm({ open: false, loading: false })}
                onConfirm={confirmDeleteTask}
                title="Delete Task?"
                message={`Are you sure you want to delete "${task?.title || 'this task'}"? This action cannot be undone.`}
                confirmText="Delete"
                loading={deleteTaskConfirm.loading}
                variant="danger"
            />
        </div>
    );
}
